/**
 * Main Widget component.
 * Orchestrates schema loading, form rendering, and execution.
 */

import { useEffect, useState, useCallback, useRef } from 'preact/hooks'
import type { FunctionComponent } from 'preact'
import { EmbedApiClient, EmbedApiError } from '../core/api'
import { executeAppStream } from '../core/sse-client'
import { renderMarkdown } from '../core/markdown'
import {
  UNSUPPORTED_FIELDS_TITLE,
  missingRequired,
  unsupportedFieldsMessage,
  unsupportedFieldsReason,
  unsupportedRequired,
} from '../core/inputs'
import type {
  AgentBaseConfig,
  EmbedAppSchema,
  InputConfig,
  ExecutionProgress,
  SSEEvent,
} from '../types'
import { FileUploaderContext } from '../core/upload'
import { InputForm } from './InputForm'
import { OutputDisplay } from './OutputDisplay'
import { ProgressBar } from './ProgressBar'

interface WidgetApi {
  execute: () => void
  reset: () => void
  setInput: (name: string, value: unknown) => void
  getInputs: () => Record<string, unknown>
  isExecuting: () => boolean
  getResults: () => Record<string, unknown> | null
}

interface WidgetProps {
  config: AgentBaseConfig
  instanceId: string
  onApiReady: (api: WidgetApi) => void
}

type WidgetState = 'loading' | 'ready' | 'executing' | 'complete' | 'error'

const LOAD_FAILED_TITLE = 'Widget\u2019en kunne ikke indlæses'

/** Icon + title + text — the empty/terminal-state anatomy from the UX contract. */
const Notice: FunctionComponent<{ title: string; text: string }> = ({ title, text }) => (
  <div class="syv-empty">
    <div class="syv-empty-icon" aria-hidden="true">
      ⌸
    </div>
    <strong class="syv-empty-title">{title}</strong>
    <span class="syv-empty-text">{text}</span>
  </div>
)

/** Where the branding line points. Not `window.location.origin` — that is the
 *  embedding site, not AgentBase. */
const AGENTBASE_URL = 'https://agentbase.dk'

export const Widget: FunctionComponent<WidgetProps> = ({
  config,
  // instanceId reserved for future scoped styling
  onApiReady,
}) => {
  const [state, setState] = useState<WidgetState>('loading')
  const [schema, setSchema] = useState<EmbedAppSchema | null>(null)
  const [inputs, setInputs] = useState<Record<string, unknown>>({})
  const [results, setResults] = useState<Record<string, unknown> | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [errorTitle, setErrorTitle] = useState<string | null>(null)
  const [progress, setProgress] = useState<ExecutionProgress | null>(null)

  const abortControllerRef = useRef<AbortController | null>(null)
  // True once the run has produced a verdict (complete/error). The stream can
  // end cleanly without one — a backend restart mid-run, a proxy closing an idle
  // body — and the closure below cannot read `state` (stale at execute time).
  const settledRef = useRef(false)
  const apiClient = useRef(
    new EmbedApiClient({
      token: config.token,
      baseUrl: config.apiBaseUrl,
    })
  )

  // A picked file goes up once and the run names it by id.
  const uploadFile = useCallback(
    (file: File) => apiClient.current.uploadFile(config.appId, file),
    [config.appId]
  )

  // Initialize default values from schema
  const initializeInputs = useCallback((schema: EmbedAppSchema) => {
    const defaults: Record<string, unknown> = {}
    for (const input of schema.inputs) {
      if (input.default !== undefined) {
        defaults[input.name] = input.default
      }
    }
    setInputs(defaults)
  }, [])

  // Load the schema. Held in a ref so the error state can re-run it: the widget
  // sits on someone else's page, where there is no surrounding AgentBase UI to
  // navigate and nothing tells the visitor that reloading would help. A failed
  // load without a retry is a dead end.
  const loadSchemaRef = useRef<() => void>(() => {})

  useEffect(() => {
    const loadSchema = async () => {
      setState('loading')
      setError(null)
      setErrorTitle(null)
      try {
        const data = await apiClient.current.getSchema(config.appId)
        setSchema(data)
        initializeInputs(data)
        setState('ready')
      } catch (err) {
        // The client already produced a showable Danish message; a rejected
        // origin gets its own heading so the site owner is not left reading it
        // as a generic outage.
        setError(err instanceof Error ? err.message : LOAD_FAILED_TITLE)
        setErrorTitle(
          err instanceof EmbedApiError && err.isOriginRejected
            ? 'Domænet har ikke adgang'
            : LOAD_FAILED_TITLE
        )
        setState('error')
        config.onExecutionError?.({
          message: err instanceof Error ? err.message : 'Ukendt fejl',
          code: err instanceof EmbedApiError ? String(err.status) : undefined,
        })
      }
    }

    loadSchemaRef.current = loadSchema
    loadSchema()
    // config is intentionally not a dependency: the callbacks are read fresh on
    // each call, and re-running this effect would refetch the schema on every
    // render of a host that passes inline handlers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [config.appId, initializeInputs])

  // Handle input changes
  const handleInputChange = useCallback(
    (name: string, value: unknown) => {
      setInputs((prev) => ({ ...prev, [name]: value }))
      config.onInputChange?.(name, value)
    },
    [config]
  )

  // Execute the flow
  const execute = useCallback(async () => {
    if (!schema || state === 'executing') return

    // The run door refuses a missing required value too, but names the field's
    // slug ("'fil' skal udfyldes"). The user only ever saw the label, so check
    // here first and say the word they are looking at.
    // Reachable via the host page's widget.execute() even though the button is
    // withheld; the answer has to be the same sentence either way.
    const unsupported = unsupportedRequired(schema.inputs, inputs)
    if (unsupported.length > 0) {
      setErrorTitle(null)
      setError(unsupportedFieldsMessage(unsupported))
      setState('error')
      return
    }

    const missing = missingRequired(schema.inputs, inputs)
    if (missing.length > 0) {
      setErrorTitle(null)
      setError(
        missing.length === 1
          ? `Udfyld "${missing[0]}" for at køre.`
          : `Udfyld ${missing.map((label) => `"${label}"`).join(', ')} for at køre.`
      )
      setState('error')
      return
    }

    setState('executing')
    settledRef.current = false
    setError(null)
    setErrorTitle(null)
    setResults(null)
    // totalNodes starts at 0 — unknown. The run reports the real count on its
    // first node_start; deriving it from the form's field count produced a
    // percentage that tracked nothing.
    setProgress({ currentNode: '', completedNodes: 0, totalNodes: 0, percentage: 0 })

    config.onExecutionStart?.()

    const streamUrl = apiClient.current.getStreamUrl(config.appId)

    abortControllerRef.current = executeAppStream(streamUrl, inputs, {
      onEvent: (event: SSEEvent) => {
        switch (event.type) {
          case 'node_start':
            setProgress((prev) =>
              prev
                ? {
                    ...prev,
                    currentNode: event.node_name ?? event.node_id,
                    totalNodes: event.total_nodes ?? prev.totalNodes,
                  }
                : null
            )
            break

          case 'node_complete':
            setProgress((prev) => {
              if (!prev) return null
              const completed = prev.completedNodes + 1
              const newProgress = {
                ...prev,
                completedNodes: completed,
                percentage:
                  prev.totalNodes > 0
                    ? Math.round((completed / prev.totalNodes) * 100)
                    : 0,
              }
              config.onExecutionProgress?.(newProgress)
              return newProgress
            })
            break

          case 'flow_complete':
            settledRef.current = true
            setResults(event.results)
            setState('complete')
            config.onExecutionComplete?.(event.results)
            break

          case 'flow_error':
            settledRef.current = true
            setError(event.error)
            setState('error')
            config.onExecutionError?.({ message: event.error })
            break

          case 'node_error':
            settledRef.current = true
            setError(`Error in ${event.node_name ?? event.node_id}: ${event.error}`)
            setState('error')
            config.onExecutionError?.({
              message: event.error,
              code: event.node_id,
            })
            break
        }
      },
      onError: (err) => {
        settledRef.current = true
        setError(err.message)
        setState('error')
        config.onExecutionError?.({ message: err.message })
      },
      onComplete: () => {
        // A clean end of stream with no verdict. Left alone, the widget sat in
        // "executing" with both buttons disabled and no way out short of the
        // host page calling widget.reset().
        if (settledRef.current) return
        settledRef.current = true
        const message =
          'Forbindelsen til AgentBase blev afbrudt, før kørslen var færdig. Prøv igen.'
        setError(message)
        setState('error')
        config.onExecutionError?.({ message })
      },
    })
  }, [schema, state, inputs, config])

  // Reset the form
  const reset = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    if (schema) {
      initializeInputs(schema)
    }
    setResults(null)
    setError(null)
    setErrorTitle(null)
    setProgress(null)
    setState('ready')
  }, [schema, initializeInputs])

  // Expose API to parent
  useEffect(() => {
    onApiReady({
      execute,
      reset,
      setInput: handleInputChange,
      getInputs: () => inputs,
      isExecuting: () => state === 'executing',
      getResults: () => results,
    })
  }, [execute, reset, handleInputChange, inputs, state, results, onApiReady])

  // Render loading state
  if (state === 'loading') {
    return (
      <div class="syv-form">
        <div class="syv-spinner" />
      </div>
    )
  }

  // Render error state (schema load failed)
  if (state === 'error' && !schema) {
    return (
      <div class="syv-form">
        <div class="syv-error">
          <strong class="syv-error-title">
            {errorTitle || LOAD_FAILED_TITLE}
          </strong>
          <span>{error}</span>
        </div>
        <div class="syv-buttons">
          <button
            type="button"
            class="syv-button syv-button-secondary"
            onClick={() => loadSchemaRef.current()}
          >
            Prøv igen
          </button>
        </div>
      </div>
    )
  }

  if (!schema) return null

  const showProgress = config.showProgress !== false && state === 'executing'
  const showLabels = config.showLabels !== false
  const theme = config.theme ?? {}
  // The compact layout is a bare form by design (it exists to sit in a sidebar),
  // so the app header is suppressed there — matching the setup page's preview.
  const showHeader = config.layout !== 'compact'
  const manualHtml =
    showHeader && theme.showUserManual !== false && schema.user_manual
      ? renderMarkdown(schema.user_manual)
      : ''

  // An app whose surface exposes no enabled fields renders as a lone run button
  // otherwise — a blank screen with an action and no explanation, which is the
  // state that took a database query to diagnose during testing.
  const visibleInputs = getOrderedInputs(schema.inputs, config.fieldOrder).filter(
    (input) => input.enabled
  )
  const hasFields = visibleInputs.length > 0
  // A required field the widget cannot fill (an entity picker needs a signed-in
  // user) makes the run unwinnable. The next move is the site owner's, in the
  // app's setup — so no run button, and a notice that says whose move it is.
  const unsupported = unsupportedRequired(schema.inputs, inputs)
  const canRun = hasFields && unsupported.length === 0

  return (
    <div class="syv-form">
      {showHeader && (
        <div class="syv-header">
          {schema.app_name && <h2 class="syv-title">{schema.app_name}</h2>}
          {theme.showDescription !== false && schema.description && (
            <p class="syv-app-description">{schema.description}</p>
          )}
        </div>
      )}

      {manualHtml && (
        <div class="syv-manual" dangerouslySetInnerHTML={{ __html: manualHtml }} />
      )}

      {/* Progress indicator */}
      {showProgress && progress && (
        <ProgressBar
          percentage={progress.totalNodes > 0 ? progress.percentage : null}
          currentNode={progress.currentNode}
        />
      )}

      {/* Input form, or an explanation of why there isn't one */}
      {hasFields ? (
        <FileUploaderContext.Provider value={uploadFile}>
          <InputForm
            inputs={visibleInputs}
            values={inputs}
            onChange={handleInputChange}
            showLabels={showLabels}
            disabled={state === 'executing'}
          />
        </FileUploaderContext.Provider>
      ) : (
        <Notice
          title="Appen har ingen felter at udfylde"
          text="Åbn appens opsætning i AgentBase, og vælg hvilke felter widget'en skal vise."
        />
      )}

      {/* Error message */}
      {error && state === 'error' && (
        <div class="syv-error">
          {errorTitle && <strong class="syv-error-title">{errorTitle}</strong>}
          <span>{error}</span>
        </div>
      )}

      {hasFields && unsupported.length > 0 && (
        <Notice title={UNSUPPORTED_FIELDS_TITLE} text={unsupportedFieldsReason(unsupported)} />
      )}

      {/* Buttons — withheld when there is no form to submit, or when the form
          can never be completed here: a run button that cannot lead anywhere is
          worse than no button. */}
      {canRun && (
      <div class="syv-buttons">
        <button
          type="button"
          class="syv-button syv-button-primary"
          onClick={execute}
          disabled={state === 'executing'}
        >
          {state === 'executing' ? (
            <>
              <span class="syv-spinner" />{' '}
              {config.submitButtonText || 'Kører…'}
            </>
          ) : (
            config.submitButtonText || 'Kør'
          )}
        </button>
        {config.resetButtonText !== '' && (
          <button
            type="button"
            class="syv-button syv-button-secondary"
            onClick={reset}
            disabled={state === 'executing'}
          >
            {config.resetButtonText || 'Nulstil'}
          </button>
        )}
      </div>
      )}

      {/* Output display */}
      {results && schema.outputs.length > 0 && (
        <OutputDisplay outputs={schema.outputs} results={results} />
      )}

      {theme.showBranding !== false && (
        <p class="syv-branding">
          Bygget med{' '}
          <a href={AGENTBASE_URL} target="_blank" rel="noopener noreferrer">
            AgentBase
          </a>
        </p>
      )}
    </div>
  )
}

/**
 * Get inputs in the specified order, with unspecified inputs at the end.
 */
function getOrderedInputs(
  inputs: InputConfig[],
  fieldOrder?: string[]
): InputConfig[] {
  if (!fieldOrder || fieldOrder.length === 0) {
    return inputs
  }

  const ordered: InputConfig[] = []
  const remaining = [...inputs]

  for (const name of fieldOrder) {
    const index = remaining.findIndex((i) => i.name === name)
    if (index !== -1) {
      ordered.push(remaining[index])
      remaining.splice(index, 1)
    }
  }

  return [...ordered, ...remaining]
}
